import React, { useState, useRef, useEffect } from "react";
import "~/css/Accordion.css";
import ConversationEndExplanationModal from "~/components/modal/ConversationEndExplanationModal";
import { PlaySound } from "~/SoundUtils";
import * as icon from "~/Icons";
import { useAtom } from "jotai";
import { volumeAtom, isReceptionAtom } from "~/Atom";

// アニメーションの時間定数
const transitionDuration = "0.5s";
const transitionTiming = "ease";

interface AccordionItemProps {
  title: string | React.JSX.Element;
  children: React.ReactNode;
  isInfo: boolean;
  information: string;
  dynamicHeight?: boolean; // dynamicHeightがtrueの場合に限り、子要素の変更に応じて高さを動的に調整します。
  isChildIcon?: boolean; // アイコンの種類
  autoHeight?: boolean; // autoHeightがtrueの場合、コンテンツの高さを自動で調整します。
}

/**
 * アコーディオン項目コンポーネント
 * クリックで開閉するコンテンツ領域を提供する
 *
 * @param title - アコーディオンのタイトル
 * @param children - アコーディオン内に表示するコンテンツ
 * @param isInfo - 情報モーダルを表示するかどうか
 * @param dynamicHeight - コンテンツ変更時に高さを動的に調整するかどうか
 * @param isChildIcon - 子要素のアイコンかどうか
 * @param autoHeight - コンテンツの高さを自動で調整するかどうか
 */
const AccordionItem: React.FC<AccordionItemProps> = ({
  title,
  children,
  isInfo,
  dynamicHeight = false,
  isChildIcon = false, // アイコンの種類
  autoHeight = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [volume] = useAtom(volumeAtom);
  const [isReception] = useAtom(isReceptionAtom);
  const [height, setHeight] = useState<string>("0px");

  /**
   * アコーディオンの開閉を切り替える
   */
  const toggleAccordion = () => {
    setIsOpen(!isOpen);
    PlaySound("push", volume);
  };

  /**
   * 情報モーダルを開く
   */
  const openModal = () => {
    setIsModalOpen(true);
  };

  /**
   * 情報モーダルを閉じる
   */
  const closeModal = () => {
    setIsModalOpen(false);
    PlaySound("push", volume);
  };

  /**
   * アコーディオン開閉時の高さ調整
   */
  useEffect(() => {
    if (!contentRef.current) return;

    if (isOpen) {
      // 開いた状態の高さを設定
      const scrollHeight = contentRef.current.scrollHeight;
      setHeight(`${scrollHeight}px`);

      if (autoHeight) {
        // autoHeightの場合、アニメーション完了後にautoに変更
        setTimeout(() => {
          setHeight("auto");
        }, 500); // transitionDurationと同じ時間
      }
    } else {
      if (autoHeight) {
        // 閉じる際もスムーズなアニメーションのため2段階処理
        const scrollHeight = contentRef.current.scrollHeight;
        requestAnimationFrame(() => {
          setHeight(`${scrollHeight}px`);
          requestAnimationFrame(() => setHeight("0px"));
        });
      } else {
        // 閉じた状態は高さ0
        setHeight("0px");
      }
    }
  }, [isOpen, children, dynamicHeight, autoHeight]);

  const isOpenClassName = isOpen ? "is-active" : "";
  const receptionClassName = isReception ? "accordion-summary-reception" : "";
  const receptionIconClassName = isReception ? "accordion-icon-parent-reception" : "";

  return (
    <div className="accordion-item">
      <div
        className={`accordion-summary ${isOpenClassName} ${receptionClassName}`}
        onClick={toggleAccordion}
        aria-expanded={isOpen}
      >
        <div className="accordion-title-wrapper">
          <span className="accordion-title">{title}</span>
          {isInfo && (
            <button
              type="button"
              className="accordion-info-button"
              aria-label="詳細情報を表示"
              onClick={(e) => {
                e.stopPropagation();
                openModal();
                PlaySound("push", volume);
              }}
              onMouseUp={(e) => {
                e.stopPropagation();
              }}
            >
              <icon.icons_info />
            </button>
          )}
        </div>
        {isChildIcon ? (
          <span className="material-symbols-rounded accordion-icon-child">stat_minus_1</span>
        ) : (
          <span
            className={`material-symbols-rounded accordion-icon-parent ${receptionIconClassName}`}
          >
            stat_minus_1
          </span>
        )}
      </div>

      <ConversationEndExplanationModal isOpen={isModalOpen} onClose={closeModal} />

      <div
        ref={contentRef}
        className="accordion-content"
        style={{
          height: height,
          transition: `height ${transitionDuration} ${transitionTiming}`,
        }}
      >
        <div className="content-wrapper">{children}</div>
      </div>
    </div>
  );
};

export default AccordionItem;
